import { forwardRef } from "@chakra-ui/react";
import type { LinkWrapperProps as NextLinkProps } from "../../navigation/link/LinkWrapper";
import type { BoxProps } from "../../layout/box/Box";
import { Box } from "../../layout/box/Box";
import type { TextProps } from "../../content/text/Text";
import { Text } from "../../content/text/Text";
import type { MoneyProps } from "../../data-display/money/Money";
import { Money } from "../../data-display/money/Money";
import type { NextImageProps } from "../../media-and-icons/image/NextImage";
import { LinkWrapper } from "../../navigation/link/LinkWrapper";
import { cx } from "../../../utils/chakra";
import { productRowStyles as styles } from "./ProductRow.styles";
import { ProductImageFrame } from "../productImage/ProductImageFrame";

export type ProductRowProps = BoxProps;
export type ProductRowLinkProps = NextLinkProps;
export type ProductRowImageProps = NextImageProps & {
    backgroundColor?: string;
    sx?: { [x: string]: any };
    minH?: string;
};
export type ProductRowTextProps = TextProps;
export type ProductRowColorSizeProps = BoxProps & {
    // named colorProperty to avoid conflict with color prop
    colorProperty?: {
        prefix?: string;
        value?: string;
    };
    size?: {
        prefix?: string;
        value?: string;
    };
    productNumber?: string;
    quantity?: string;
};
export interface ProductRowPriceProps extends BoxProps {
    priceBefore?: MoneyProps;
    priceNow: MoneyProps;
}

export const ProductRow = forwardRef<ProductRowProps, "div">(({ ...props }, ref) => {
    return <Box ref={ref} sx={styles.root} data-testid={"product-row"} {...props} />;
});

export const ProductRowLink = forwardRef<ProductRowLinkProps, "a">((props, ref) => {
    const { children, ...rest } = props;
    return (
        <LinkWrapper
            role="group"
            className={cx("chakra-product-row", props.className)}
            sx={styles.link}
            ref={ref}
            {...rest}
            style={{ outlineOffset: "6px" }}
        >
            {children}
        </LinkWrapper>
    );
});

export const ProductRowImage = forwardRef<ProductRowImageProps, "img">(({ ...props }, ref) => {
    const { sizes = ["100px"], width = "100px" } = props;
    return (
        <Box
            sx={{ ...styles.imageContainer }}
            width={width}
            data-testid={"product-image"}
            ref={ref}
        >
            <ProductImageFrame src={props.src as string} alt={props.alt} sizes={sizes} />
        </Box>
    );
});

export const ProductRowHeadline = forwardRef<ProductRowTextProps, "div">(
    ({ children, ...props }, ref) => {
        return (
            <Text sx={styles.headline} data-testid={"headline"} ref={ref} {...props}>
                {children}
            </Text>
        );
    }
);

export const ProductRowPrice = forwardRef<ProductRowPriceProps, "div">(({ ...props }, ref) => {
    const { priceBefore, priceNow, ...rest } = props;
    return (
        <Box ref={ref} sx={styles.price} {...rest}>
            <Money as={"span"} sx={styles.priceNow} data-testid={"price-now"} {...priceNow} />
            {priceBefore && (
                <Money
                    as={"span"}
                    sx={styles.priceBefore}
                    data-testid={"price-before"}
                    {...priceBefore}
                />
            )}
        </Box>
    );
});

export const ProductRowDescription = forwardRef<ProductRowTextProps, "div">(({ ...props }, ref) => {
    return <Text sx={styles.description} data-testid={"description"} ref={ref} {...props} />;
});
export const ProductRowMeta = forwardRef<ProductRowColorSizeProps, "div">(({ ...props }, ref) => {
    const { colorProperty, size, quantity, productNumber, sx = {}, ...rest } = props;
    if (!colorProperty && !size && !productNumber && !quantity) {
        return null;
    }

    const generateMetaLabel = (prefix?: string, value?: string) => {
        if (!value) {
            return;
        }
        if (!prefix) {
            return value;
        }
        return `${prefix}: ${value}`;
    };

    return (
        <Box ref={ref} as="p" sx={{ ...styles.metaContainer, ...sx }} {...rest}>
            {colorProperty?.value && (
                <Text as="span" sx={styles.metaStyle} data-testid="color">
                    {generateMetaLabel(colorProperty.prefix, colorProperty.value)}
                </Text>
            )}
            {size?.value && (
                <Text as="span" sx={styles.metaStyle} data-testid="size">
                    {generateMetaLabel(size.prefix, size.value)}
                </Text>
            )}
            {productNumber && (
                <Text as="span" sx={styles.metaStyle} data-testid="itemNo">
                    ItemNo: {productNumber}
                </Text>
            )}
            {quantity && (
                <Text as="span" sx={styles.metaStyle} data-testid="quantity">
                    Qty: {quantity}
                </Text>
            )}
        </Box>
    );
});

export const GiftWrappingPrice = forwardRef<ProductRowPriceProps, "div">(
    ({ priceBefore, priceNow, ...props }, ref) => {
        return (
            <ProductRowPrice
                {...props}
                ref={ref}
                priceBefore={priceBefore}
                priceNow={priceNow}
                sx={{
                    '[data-testid="price-before"]': {
                        marginLeft: 2,
                    },
                    '[data-testid="price-now"]': {
                        marginRight: 0,
                    },
                }}
            />
        );
    }
);
